import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
  LinearProgress
} from '@mui/material';
import { 
  CheckCircle, 
  Cancel, 
  DirectionsBus,
  InfoOutlined
} from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
// Import API functions
import { getTransportLineMetrics, getPendingTransportLines, authorizeTransportLine } from '../api';

const Tickets = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Tickets');
  const [searchValue, setSearchValue] = useState('');
  const [pendingTransportLines, setPendingTransportLines] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const navigate = useNavigate();

  // Colors for the metrics cards
  const COLORS = {
    active: '#4caf50',
    inactive: '#ff9800',
    blocked: '#f44336',
    pending: '#2196f3'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch metrics using API function
      const metricsData = await getTransportLineMetrics();
      
      // Fetch pending transport lines using API function
      const pendingData = await getPendingTransportLines();
      
      setMetrics(metricsData);
      setPendingTransportLines(pendingData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (line) => {
    setSelectedLine(line);
    setConfirmDialogOpen(true);
  };

  const handleReject = (line) => {
    setSelectedLine(line);
    setRejectDialogOpen(true);
  };

  const confirmApprove = async () => {
    try {
      await authorizeTransportLine(selectedLine.id, 'Aprobado');
      toast.success('Línea de transporte autorizada correctamente');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error authorizing transport line:', error);
      toast.error('Error en la comunicación con el servidor');
    } finally {
      setConfirmDialogOpen(false);
      setSelectedLine(null);
    }
  };

  const confirmReject = async () => {
    try {
      await authorizeTransportLine(selectedLine.id, 'Rechazado');
      toast.success('Línea de transporte rechazada y eliminada');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error rejecting transport line:', error);
      toast.error('Error en la comunicación con el servidor');
    } finally {
      setRejectDialogOpen(false);
      setSelectedLine(null);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // Calculate total for percentages
  const calculateTotal = () => {
    if (!metrics) return 0;
    return metrics.active + metrics.inactive + metrics.blocked + metrics.pending_authorization;
  };
  
  const total = calculateTotal();
  
  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />
      <div className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        <Topbar 
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
        
        <Typography variant="h4" gutterBottom sx={{ mt: 2, mb: 4 }}>
          Panel de Autorización de Transportes
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {/* Metrics Section */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Métricas de Líneas de Transporte
                  </Typography>
                  {metrics ? (
                    <>
                      {/* Replace Recharts with simple MUI components */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Card sx={{ bgcolor: COLORS.active, color: 'white', mb: 2 }}>
                            <CardContent>
                              <Typography variant="h5">{metrics.active}</Typography>
                              <Typography>Activas</Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={total ? (metrics.active / total) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card sx={{ bgcolor: COLORS.inactive, color: 'white', mb: 2 }}>
                            <CardContent>
                              <Typography variant="h5">{metrics.inactive}</Typography>
                              <Typography>Pausadas</Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={total ? (metrics.inactive / total) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card sx={{ bgcolor: COLORS.blocked, color: 'white', mb: 2 }}>
                            <CardContent>
                              <Typography variant="h5">{metrics.blocked}</Typography>
                              <Typography>Bloqueadas</Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={total ? (metrics.blocked / total) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card sx={{ bgcolor: COLORS.pending, color: 'white', mb: 2 }}>
                            <CardContent>
                              <Typography variant="h5">{metrics.pending_authorization}</Typography>
                              <Typography>Por Autorizar</Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={total ? (metrics.pending_authorization / total) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Distribución de Líneas de Transporte
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: COLORS.active, mr: 1 }}></Box>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>Activas</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {total ? ((metrics.active / total) * 100).toFixed(1) : 0}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={total ? (metrics.active / total) * 100 : 0}
                              sx={{ height: 10, borderRadius: 1, mb: 1, bgcolor: '#f0f0f0' }} 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: COLORS.inactive, mr: 1 }}></Box>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>Pausadas</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {total ? ((metrics.inactive / total) * 100).toFixed(1) : 0}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={total ? (metrics.inactive / total) * 100 : 0}
                              sx={{ height: 10, borderRadius: 1, mb: 1, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: COLORS.inactive } }} 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: COLORS.blocked, mr: 1 }}></Box>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>Bloqueadas</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {total ? ((metrics.blocked / total) * 100).toFixed(1) : 0}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={total ? (metrics.blocked / total) * 100 : 0}
                              sx={{ height: 10, borderRadius: 1, mb: 1, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: COLORS.blocked } }} 
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: COLORS.pending, mr: 1 }}></Box>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>Por Autorizar</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {total ? ((metrics.pending_authorization / total) * 100).toFixed(1) : 0}%
                              </Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={total ? (metrics.pending_authorization / total) * 100 : 0}
                              sx={{ height: 10, borderRadius: 1, mb: 1, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: COLORS.pending } }} 
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  ) : (
                    <Typography>No hay datos disponibles</Typography>
                  )}
                </Paper>
              </Grid>
              
              {/* Pending Authorizations */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DirectionsBus sx={{ mr: 1 }} />
                    Líneas de Transporte Pendientes
                  </Typography>
                  
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {pendingTransportLines.length > 0 ? (
                      <TableContainer sx={{ flexGrow: 1 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Nombre</TableCell>
                              <TableCell>Razón Social</TableCell>
                              <TableCell>Alta SAP</TableCell>
                              <TableCell width="210px">Acciones</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pendingTransportLines.map((line) => (
                              <TableRow key={line.id}>
                                <TableCell>{line.name}</TableCell>
                                <TableCell>{line.razon_Social}</TableCell>
                                <TableCell>{line.alta_SAP}</TableCell>
                                <TableCell sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckCircle />}
                                    onClick={() => handleApprove(line)}
                                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                                  >
                                    Autorizar
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    startIcon={<Cancel />}
                                    onClick={() => handleReject(line)}
                                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                                  >
                                    Rechazar
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 4,
                        flexGrow: 1
                      }}>
                        <InfoOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          No hay líneas de transporte pendientes de autorización
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      color="primary" 
                      variant="outlined" 
                      onClick={fetchData}
                    >
                      Actualizar datos
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
        
        {/* Approve Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
        >
          <DialogTitle>Confirmar Autorización</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro que desea autorizar la línea de transporte "{selectedLine?.name}"?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
            <Button onClick={confirmApprove} color="success" variant="contained">
              Autorizar
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Reject Dialog - simplified without reason field */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
        >
          <DialogTitle>Rechazar y Eliminar Línea de Transporte</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro que desea rechazar y eliminar la línea de transporte "{selectedLine?.name}"?
              Esta acción eliminará permanentemente la línea de transporte de la base de datos.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Cancelar</Button>
            <Button 
              onClick={confirmReject} 
              color="error" 
              variant="contained"
            >
              Rechazar y Eliminar
            </Button>
          </DialogActions>
        </Dialog>
        
        <ToastContainer />
      </div>
    </div>
  );
};

export default Tickets;